import React from 'react';
import { AccessLevel } from '@dev/ee-api/ee-api-models';
import { useTranslation } from 'react-i18next';
import { capitalizeString } from '@dev/ee-tools';

import {
    StartIcon,
    StatisticsIcon,
    ContactsIcon,
    CampaignIcon,
    AutomationsIcon,
    ExportsIcon,
    SettingsIcon,
    VerificationsIcon,
    TemplatesIcon,
    FormsIcon,
    WindowIcon,
} from '@dev/ee-components';
import { 
    CheckoutsIcon, 
    PaidNewsletterIcon
} from '@dev/ee-components';
import {
    APIPaths,
    AutomationPaths,
    CampaignCreatorType,
    CampaignsPaths,
    CheckoutsPaths,
    ContactsPaths,
    EVSPaths,
    ExportsPaths,
    FormsPaths,
    HomePaths,
    LandingPagePaths,
    PaidNewslettersPaths,
    SettingsPaths,
    TemplatesPaths,
} from '../dictionaries/Paths';
import { useUpselling } from '../hooks/useUpselling';
import { useDashboardElementVisibility } from '../hooks/useDashboardElementVisibility';

export interface RouteConfType {
    label: string;
    icon?: JSX.Element;
    url?: string;
    hideOnMobile?: boolean;
    isLegacy?: boolean;
    /**
     * if route is in fact a route with param, but we want to get menu item displayed as activated no mather what param is the default one
     */
    memberOf?: string[];
    accessLevel?: AccessLevel[];
    subMenuItems?: Omit<RouteConfType, 'subMenuItems'>[];
    showUpsellingIfNotActive?: boolean;
}

export const useMenuItems = () => {
    const { t } = useTranslation();

    const { isCreatorMenuVisible } = useDashboardElementVisibility();
    const { showUpsellingIfNotActive } = useUpselling();

    if (isCreatorMenuVisible) {
        return [
            {
                label: t('start'),
                icon: <StartIcon />,
                url: HomePaths.Home,
                accessLevel: [AccessLevel.ViewAccount],
            },
            {
                label: t('audience'),
                url: ContactsPaths.Contacts,
                icon: <ContactsIcon />,
                accessLevel: [AccessLevel.ViewContacts, AccessLevel.ViewForms, AccessLevel.ViewLandingPages],
            },
            {
                label: t('linkinbio'),
                url: LandingPagePaths.LandingPageList,
                icon: <WindowIcon />,
                accessLevel: [AccessLevel.ViewLandingPages],
            },
            {
                label: t('forms'),
                url: FormsPaths.FormList,
                icon: <FormsIcon />,
                accessLevel: [AccessLevel.ViewForms],
            },
            {
                label: t('campaigns'),
                url: CampaignsPaths.NewCampaigns,
                icon: <CampaignIcon />,
                accessLevel: [AccessLevel.ViewCampaigns, AccessLevel.ViewTemplates],
            },
            {
                label: t('emailtemplates'),
                url: TemplatesPaths.TemplateList,
                icon: <TemplatesIcon />,
                accessLevel: [AccessLevel.ViewTemplates],
            },
            {
                label: t('automations'),
                icon: <AutomationsIcon />,
                url: AutomationPaths.AutomationsList,
                accessLevel: [AccessLevel.ViewAutomations],
                showUpsellingIfNotActive,
            },
            {
                label: t('paidnewsletters'),
                icon: <PaidNewsletterIcon />,
                url: PaidNewslettersPaths.PaidNewslettersList,
                accessLevel: [AccessLevel.ViewPaidNewsletters],
            },
            {
                label: t('checkouts'),
                icon: <CheckoutsIcon />,
                url: CheckoutsPaths.CheckoutsList,
                accessLevel: [AccessLevel.ViewLandingPagesCheckout],
            },
        ];
    }

    return [
        {
            label: capitalizeString(t('start')),
            icon: <StartIcon />,
            url: HomePaths.Home,
            accessLevel: [AccessLevel.ViewAccount],
        },
        {
            label: capitalizeString(t('activity')),
            icon: <StatisticsIcon />,
            url: APIPaths.Statistics,
            accessLevel: [AccessLevel.ViewReports],
        },
        {
            label: capitalizeString(t('audience')),
            icon: <ContactsIcon />,
            accessLevel: [AccessLevel.ViewContacts, AccessLevel.ViewForms, AccessLevel.ViewLandingPages],
            subMenuItems: [
                {
                    label: capitalizeString(t('contacts')),
                    url: ContactsPaths.ContactsOverview,
                    memberOf: ['/contacts'],
                    accessLevel: [AccessLevel.ViewContacts],
                },
                {
                    label: capitalizeString(t('forms')),
                    url: FormsPaths.FormList,
                    memberOf: ['/legacy/#/forms'],
                    accessLevel: [AccessLevel.ViewForms],
                },
                {
                    label: capitalizeString(t('landingpages')),
                    url: LandingPagePaths.LandingPageList,
                    memberOf: ['/legacy/#/landingpages'],
                    accessLevel: [AccessLevel.ViewLandingPages],
                },
            ],
        },
        {
            label: capitalizeString(t('campaigns')),
            icon: <CampaignIcon />,
            accessLevel: [AccessLevel.ViewCampaigns, AccessLevel.ViewTemplates],
            subMenuItems: [
                {
                    label: capitalizeString(t('allcampaigns')),
                    url: '/campaigns/new/',
                    memberOf: [`/new/${CampaignCreatorType.AB}`, `/new/${CampaignCreatorType.REGULAR}`],
                    accessLevel: [AccessLevel.ViewCampaigns],
                },
                {
                    label: capitalizeString(t('emailtemplates')),
                    url: TemplatesPaths.TemplateList,
                    memberOf: ['/legacy/#/templates'],
                    accessLevel: [AccessLevel.ViewTemplates],
                },
            ],
        },
        {
            label: t('paidnewsletters'),
            icon: <PaidNewsletterIcon />,
            url: PaidNewslettersPaths.PaidNewslettersList,
            accessLevel: [AccessLevel.ViewPaidNewsletters],
        },
        {
            label: t('checkouts'),
            icon: <CheckoutsIcon />,
            url: CheckoutsPaths.CheckoutsList,
            accessLevel: [AccessLevel.ViewLandingPagesCheckout],
        },
        {
            label: t('automations'),
            icon: <AutomationsIcon />,
            url: AutomationPaths.AutomationsList,
            accessLevel: [AccessLevel.ViewAutomations],
            showUpsellingIfNotActive,
        },
        {
            label: t('verifications'),
            icon: <VerificationsIcon />,
            url: EVSPaths.VerificationsTabs,
            accessLevel: [AccessLevel.ViewVerifyEmails],
        },
        {
            label: t('exports'),
            icon: <ExportsIcon />,
            url: ExportsPaths.Exports,
            accessLevel: [AccessLevel.Export],
        },
        {
            label: capitalizeString(t('settings')),
            icon: <SettingsIcon />,
            url: SettingsPaths.Settings,
            accessLevel: [AccessLevel.ViewSettings],
        },
    ];
};
